import { Mods, mods, theme } from '@approvalmax/theme';
import { AmCircleLogo, QBooksLogo } from '@approvalmax/ui';
import { StyledMods } from '@styled-kit/mods';
import { selectors, ui } from 'modules/common';
import { Cin7Logo, DextLogoIcon, EyeShowOffIcon, OracleNetsuiteLogo, XeroIcon } from 'modules/sprites';
import { font, uTextEllipsis, uTransparentLink } from 'modules/styles';
import { Link } from 'react-router-dom';
import styled from 'styled-components';

const avatar = `
    flex: none;
    margin-right: 12px;
`;

const roundedBorder = `
    border-radius: 50%;
    overflow: hidden;
`;

export const Content = styled(Link)`
    ${uTransparentLink};
    display: flex;
    align-items: center;
    min-height: 93px;
    background: #fff;
    padding: 12px 24px;
    position: relative;
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    transition: background-color ${theme.duration.fast}ms ease-in-out;

    &:before,
    &:after {
        content: '';
        display: block;
        position: absolute;
        left: 24px;
        right: 0;
        height: 1px;
        background-color: ${theme.color.midnight40};
        transition: background-color ${theme.duration.fast}ms ease-in-out;
    }

    &:before {
        top: -1px;
    }

    &:after {
        bottom: -1px;
    }
`;

export const Body = styled.div`
    flex: 1;
    overflow: hidden;
`;

export const TitleRow = styled.div`
    display: flex;
    align-items: center;
    position: relative;
`;

export const Title = styled.div`
    ${font(14, '#000', 'semibold')}
    flex: 1;
`;

export const Contact = styled.div`
    ${font(14, '#000', 'semibold')}
    ${uTextEllipsis}
    flex: 1;
    margin-right: 5px;
    margin-top: -4px;
`;

export const DraftBadge = styled.div`
    ${font(9, '#fff', 'standard')}
    letter-spacing: 1px;
    flex: none;
    background: #9b9b9b;
    padding: 0 4px;
`;

export const AmountRow = styled.div`
    display: flex;
    align-items: center;
    min-height: 18px;
`;

export const Amount = styled.div`
    ${font(13, '#000', 'standard')}
    flex: 1;
`;

export const CompanyRow = styled.div`
    display: flex;
    align-items: center;
    margin-top: 3px;
`;

export const CompanyName = styled.div`
    ${font(11, '#777d81', 'semibold')}
    ${uTextEllipsis}
    flex: 1;
    margin-right: 5px;
    text-transform: uppercase;
`;

export const DeadlineWarn = styled.div<StyledMods<{ warning?: selectors.types.DueDateWarning }>>`
    flex: none;
    ${font(11, '#777d81', 'semibold')};

    ${mods('warning', selectors.types.DueDateWarning.LessThanOneDay)`
        color: #f0984f;
    `};

    ${mods('warning', selectors.types.DueDateWarning.Overdue)`
        color: #cb4e30;
    `};
`;

export const ScheduledPayment = styled.div<StyledMods<{ warning?: boolean }>>`
    flex: none;
    ${font(11, theme.color.midnight100, 'semibold')};

    ${mods('warning', true)`
        color: #cb4e30;
    `};
`;

export const Container = styled.div`
    display: flex;
`;

export const LeftSide = styled.div`
    overflow: hidden;
    flex-grow: 1;
`;

export const ReceiptBankAvatar = styled(DextLogoIcon)`
    ${avatar}
    ${roundedBorder}
`;

export const ApprovalMaxAvatar = styled(ui.Avatar)`
    ${avatar};
    background-color: ${theme.color.blue20};
    color: ${theme.color.midnight80};
`;

export const XeroAvatar = styled(XeroIcon)`
    ${avatar}
`;

export const AmCircleLogoStyled = styled(AmCircleLogo)`
    margin-right: 12px;
`;

export const NetSuiteAvatar = styled(OracleNetsuiteLogo)`
    ${avatar}
`;

export const QBooksAvatar = styled(QBooksLogo)`
    ${avatar}
`;

export const DearAvatar = styled(Cin7Logo)`
    ${avatar}
`;

export const BadgeWrapper = styled.div`
    display: flex;
    align-items: center;
    min-height: 20px;
`;

export const BadgeEyeIcon = styled(EyeShowOffIcon)`
    width: 18px;
    min-width: 18px;
    margin-right: 8px;
    color: #a0a1a1;
`;

export const Root = styled.div<StyledMods<Mods<'active'>>>`
    cursor: pointer;
    user-select: none;
    padding: 0;
    margin-top: 0;

    &,
    &:hover {
        background: transparent;
    }

    &:last-child {
        ${Content}:after {
            display: none;
        }
    }

    ${mods.active.true`
        background: #bdc5c9;
        z-index: 1;

        ${Content} {
            background: ${theme.color.silver80};

            &:after,
            &:before {
                opacity: 0;
            }
        }
    `};

    ${mods.active.false`
        &:hover {
            background: ${({ $dark }) => ($dark ? '#465560' : '#c6c8ce')};
        }

        ${Content}:hover {
            background: ${theme.color.silver80};

            &:after,
            &:before {
                opacity: 0;
            }
        }

        &:hover + * {
            ${Content} {
                &:before {
                    opacity: 0;
                }
            }
        }
    `};
`;

export const NarrationLine = styled.div`
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    ${font(13, '#7f7d7d')}
`;
